import { useEffect, useState } from 'react';
import {
  CapNhatNganSach,
  CapNhatNganSachInput,
  ChiTietCapNhatNganSach,
  ChiTietCapNhatNganSachInput
} from '@/types/schemas';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseCapNhatNganSachReturn {
  capNhatNganSachs: CapNhatNganSach[];
  isLoading: boolean;
  addCapNhatNganSach: (newCapNhatNganSach: CapNhatNganSachInput) => Promise<CapNhatNganSach>;
  updateCapNhatNganSach: (
    uuid: string,
    updatedCapNhatNganSach: Partial<CapNhatNganSachInput>
  ) => Promise<CapNhatNganSach>;
  deleteCapNhatNganSach: (uuid: string) => Promise<void>;
  refreshCapNhatNganSachs: () => Promise<void>;
  getCapNhatNganSachById: (uuid: string) => Promise<CapNhatNganSach | null>;

  // Chi tiết methods
  getChiTietByBaoCao: (baoCaoUuid: string) => Promise<ChiTietCapNhatNganSach[]>;
  addChiTiet: (chiTiet: ChiTietCapNhatNganSachInput) => Promise<ChiTietCapNhatNganSach>;
  updateChiTiet: (uuid: string, chiTiet: Partial<ChiTietCapNhatNganSachInput>) => Promise<ChiTietCapNhatNganSach>;
  deleteChiTiet: (uuid: string) => Promise<void>;
}

export const useCapNhatNganSach = (initialCapNhatNganSachs: CapNhatNganSach[] = []): UseCapNhatNganSachReturn => {
  const [capNhatNganSachs, setCapNhatNganSachs] = useState<CapNhatNganSach[]>(initialCapNhatNganSachs);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchCapNhatNganSachs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.CAP_NHAT_NGAN_SACH}/`);
      const mappedData: CapNhatNganSach[] = response.data.results;
      setCapNhatNganSachs(mappedData);
      return mappedData;
    } catch (error) {
      console.error('Error fetching budget updates:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getCapNhatNganSachById = async (uuid: string): Promise<CapNhatNganSach | null> => {
    if (!entity?.slug) return null;

    setIsLoading(true);
    try {
      const response = await api.get(`/entities/${entity.slug}/erp/${QUERY_KEYS.CAP_NHAT_NGAN_SACH}/${uuid}/`);
      return response.data as CapNhatNganSach;
    } catch (error) {
      console.error('Error fetching budget update by ID:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const addCapNhatNganSach = async (newCapNhatNganSach: CapNhatNganSachInput): Promise<CapNhatNganSach> => {
    if (!entity?.slug) throw new Error('Entity not available');

    setIsLoading(true);
    try {
      const response = await api.post(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CAP_NHAT_NGAN_SACH}/`,
        newCapNhatNganSach
      );
      const addedCapNhatNganSach: CapNhatNganSach = response.data;
      setCapNhatNganSachs(prev => [...prev, addedCapNhatNganSach]);
      return addedCapNhatNganSach;
    } catch (error) {
      console.error('Error adding budget update:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCapNhatNganSach = async (
    uuid: string,
    updatedCapNhatNganSach: Partial<CapNhatNganSachInput>
  ): Promise<CapNhatNganSach> => {
    if (!entity?.slug) throw new Error('Entity not available');

    setIsLoading(true);
    try {
      const response = await api.put(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CAP_NHAT_NGAN_SACH}/${uuid}/`,
        updatedCapNhatNganSach
      );
      const updatedData: CapNhatNganSach = response.data;
      setCapNhatNganSachs(prev => prev.map(item => (item.uuid === updatedData.uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating budget update:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCapNhatNganSach = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.CAP_NHAT_NGAN_SACH}/${uuid}/`);
      setCapNhatNganSachs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting budget update:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Chi tiết methods
  const getChiTietByBaoCao = async (baoCaoUuid: string): Promise<ChiTietCapNhatNganSach[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIET_CAP_NHAT_NGAN_SACH}/?bao_cao=${baoCaoUuid}`
      );
      return response.data.results as ChiTietCapNhatNganSach[];
    } catch (error) {
      console.error('Error fetching budget update details by report ID:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addChiTiet = async (chiTiet: ChiTietCapNhatNganSachInput): Promise<ChiTietCapNhatNganSach> => {
    if (!entity?.slug) throw new Error('Entity not available');

    setIsLoading(true);
    try {
      const response = await api.post(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIET_CAP_NHAT_NGAN_SACH}/`,
        chiTiet
      );
      return response.data as ChiTietCapNhatNganSach;
    } catch (error) {
      console.error('Error adding budget update detail:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateChiTiet = async (
    uuid: string,
    chiTiet: Partial<ChiTietCapNhatNganSachInput>
  ): Promise<ChiTietCapNhatNganSach> => {
    if (!entity?.slug) throw new Error('Entity not available');

    setIsLoading(true);
    try {
      const response = await api.patch(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIET_CAP_NHAT_NGAN_SACH}/${uuid}/`,
        chiTiet
      );
      return response.data as ChiTietCapNhatNganSach;
    } catch (error) {
      console.error('Error updating budget update detail:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteChiTiet = async (uuid: string): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.CHI_TIET_CAP_NHAT_NGAN_SACH}/${uuid}/`);
    } catch (error) {
      console.error('Error deleting budget update detail:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchCapNhatNganSachs();
  }, [entity?.slug]);

  // Wrapper function to ensure correct return type for refreshCapNhatNganSachs
  const refreshCapNhatNganSachs = async (): Promise<void> => {
    await fetchCapNhatNganSachs();
  };

  return {
    capNhatNganSachs,
    isLoading,
    addCapNhatNganSach,
    updateCapNhatNganSach,
    deleteCapNhatNganSach,
    refreshCapNhatNganSachs,
    getCapNhatNganSachById,
    getChiTietByBaoCao,
    addChiTiet,
    updateChiTiet,
    deleteChiTiet
  };
};
